import { useMemo, type CSSProperties } from 'react';
import type { ArchetypeId } from '@llura/types';

import { getArchetype } from '../lib/archetypes';

interface ArchetypeThemeProps {
  /** Spread on the wrapper element. Sets `--primary`, `--secondary`, `--accent`, `--ring`. */
  style: CSSProperties | undefined;
  /** Use as `<div data-archetype={...}>` to attach `[data-archetype="..."]` CSS rules. */
  dataArchetype: ArchetypeId | undefined;
}

/**
 * Returns scoped archetype theming. Pass `null` to fall back to global theme.
 * Theming is scoped to whichever element receives the returned `style` and
 * `data-archetype` — do not put these on `<html>` or you'll re-tint screens
 * that shouldn't change (onboarding, journal, etc.).
 */
export function useArchetypeTheme(id: ArchetypeId | null): ArchetypeThemeProps {
  return useMemo(() => {
    if (!id) return { style: undefined, dataArchetype: undefined };
    const a = getArchetype(id);
    const style: CSSProperties = {
      // CSS custom properties are typed as `string` index by React; cast is fine.
      ['--primary' as string]: a.color.primary,
      ['--secondary' as string]: a.color.secondary,
      ['--accent' as string]: a.color.accent,
      ['--ring' as string]: a.color.primary,
    };
    return { style, dataArchetype: id };
  }, [id]);
}
